import type { QueryClient } from "@tanstack/react-query";
import type { LocalStore } from "../../offline/LocalStore";
import type { AuthService } from "../auth/AuthService";

/** What a sign-out needs. */
export interface SignOutDependencies {
  /** Authentication of the person. */
  readonly auth: AuthService;
  /** Local store of the device. */
  readonly store: LocalStore;
  /** Cached server data of the session. */
  readonly queryClient: QueryClient;
  /**
   * Releases the device before the local data is deleted: push subscription, badge, delivered
   * reminders, regions (data minimization, T-188).
   */
  readonly releaseDevice: () => Promise<void>;
  /**
   * Forgets the tenant the person chose in this session.
   *
   * @param subject - OIDC subject of the person.
   */
  readonly forgetTenant: (subject: string) => void;
  /** Forgets on this device whether the person is operator staff (F-69, spec 11.4). */
  readonly forgetPlatformAccess: () => void;
}

/** The sign-out of a person and whether it is running. */
export interface SignOutFlow {
  /**
   * Signs out: the store is closed first, the device released, the local data deleted (spec 11.4)
   * and the session ended. If a step fails while the person is still signed in, the store is open
   * again and the error is passed on, so the caller can show it.
   */
  readonly signOut: () => Promise<void>;
  /**
   * Returns whether a sign-out is running.
   *
   * @returns `true` while signing out.
   */
  readonly isSigningOut: () => boolean;
}

/**
 * Creates the sign-out of the app. The store is closed before anything else, so nothing the person
 * does meanwhile is acknowledged as stored and the warning about unsent entries stays true (F-57).
 *
 * @param dependencies - What the sign-out needs.
 * @returns The sign-out.
 */
export function createSignOut(dependencies: SignOutDependencies): SignOutFlow {
  const { auth, store, queryClient } = dependencies;
  let running = false;

  const signOut = async (): Promise<void> => {
    running = true;
    store.close();

    try {
      await dependencies.releaseDevice();
      queryClient.clear();
      await store.clearAll();
      dependencies.forgetPlatformAccess();
      const user = await auth.getUser();

      if (user !== null) {
        dependencies.forgetTenant(user.subject);
      }

      await auth.logout();
    } catch (error) {
      // Still signed in: the person can record again. In doubt the store stays closed.
      const user = await auth.getUser().catch(() => null);

      if (user !== null) {
        store.reopen();
      }

      throw error;
    } finally {
      running = false;
    }
  };

  const flow: SignOutFlow = {
    signOut,
    isSigningOut: () => running,
  };

  return flow;
}

/**
 * Opens the store again whenever a person signs in, also in an app instance that a sign-out closed
 * (native apps keep the page), and then runs the start of the session. A sign-in reported while a
 * sign-out runs (a token renewed meanwhile) changes nothing (F-57).
 *
 * @param auth - Authentication of the person.
 * @param store - Local store of the device.
 * @param isSigningOut - Whether a sign-out is running.
 * @param onSignedIn - Start of the session, for example a synchronization.
 * @returns A function that ends the subscription.
 */
export function reopenOnSignIn(
  auth: AuthService,
  store: LocalStore,
  isSigningOut: () => boolean,
  onSignedIn: () => void,
): () => void {
  const unsubscribe = auth.subscribe((user) => {
    if (user !== null && !isSigningOut()) {
      store.reopen();
      onSignedIn();
    }
  });

  return unsubscribe;
}
